// https://ui.shadcn.com/docs/components/button
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/utils/tw-helpers'

const buttonVariants = cva(
  cn(
    'inline-flex items-center justify-center rounded-lg bg-[#F0F0F0] text-[17px] font-bold leading-[normal] ring-offset-blue-200 transition-colors',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
    'disabled:pointer-events-none disabled:bg-disable disabled:shadow-none disabled:text-white'
  ),
  {
    variants: {
      variant: {
        default: cn(
          'bg-primary-100 text-white',
          'active:bg-primary-pressed active:shadow-none active:translate-y-0.5'
        ),
        destructive: 'bg-[#FFD2C0] text-caution hover:bg-[#FFD2C0]/90',
        outline: cn(
          'border border-primary-100 text-primary-100 bg-white',
          'active:bg-[#EBEBEB] active:shadow-none active:translate-y-0.5'
        ),
        secondary: cn(
          'bg-accent text-primary-text',
          'active:bg-[#E6C866] active:shadow-none active:translate-y-0.5'
        ),
        ghost: 'bg-transparent',
        link: 'text-primary underline-offset-4 hover:underline',
        light: cn(
          'text-primary-100 bg-basic-bg',
          'active:bg-[#E2E4E8] active:shadow-none active:translate-y-0.5'
        ),
      },
      size: {
        default: 'h-[47px] px-4 py-2',
        sm: 'h-7 px-3 text-[13px] rounded-[4px]',
        lg: 'h-12 px-4',
        icon: 'h-10 w-10',
      },
      shadow: {
        true: 'shadow-tiny',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      shadow: true,
    },
    compoundVariants: [
      {
        variant: 'ghost',
        size: 'default',
        className: 'h-auto p-0',
      },
    ],
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, shadow = true, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        className={cn(
          buttonVariants({
            variant,
            size,
            shadow,
            className,
          })
        )}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
